import { api } from "@services/api";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import {
  type UploadedCertificate,
  type UploadedDocument,
  type UploadedFile,
} from "@services/upload/types";
import { SasTokenPrefix, SasTokenPrivilege, type SasTokenResponse } from "@services/user/types";
import { getPublicSasToken, getSasToken } from "@services/userApi";

const getErrorStatus = (error: unknown): number | undefined => {
  const parsedError = error as {
    response?: { status?: number };
    status?: number;
  };

  return parsedError.response?.status ?? parsedError.status;
};

export const uploadCertificate = async ({
  service,
  file,
  certificateType,
  transactionId,
}: {
  service: string;
  file: File;
  certificateType: string;
  transactionId?: string;
}): Promise<{ documentUrl: string; certificateType: string }> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("certificateType", certificateType);
  formData.append("name", file.name);
  formData.append("size", file.size.toString());
  formData.append("type", file.type);

  const data = await api.post<UploadedCertificate>(
    service,
    "certificates",
    formData,
    ...(transactionId ? [{ transactionId }] : []),
  );

  return {
    documentUrl: data.documentUrl,
    certificateType,
  };
};

export const uploadDocuments = async ({
  service,
  files,
  certificateType,
  transactionId,
}: {
  service: string;
  files: File[];
  certificateType: string;
  transactionId?: string;
}): Promise<{ documentUrl: string; certificateType: string }[]> => {
  const formData = new FormData();
  files.forEach((file) => {
    if (file instanceof File) {
      formData.append("files", file);
      formData.append("name", file.name);
      formData.append("size", file.size.toString());
      formData.append("type", file.type);
      formData.append("certificateType", certificateType);
    }
  });

  const data = await api.post<UploadedFile>(
    service,
    `documents`,
    formData,
    ...(transactionId ? [{ transactionId }] : []),
  );

  return (data.documentUrls || []).map((url: string) => ({
    documentUrl: url,
    certificateType,
  }));
};

export const downloadCertificate = async ({
  service,
  url,
}: {
  service: string;
  url: string;
}): Promise<Blob> => {
  let encodedUrl = "";
  try {
    encodedUrl = encodeURIComponent(decodeURIComponent(url));
  } catch {
    encodedUrl = encodeURIComponent(url);
  }
  return api.get(service, `certificates?url=${encodedUrl}`, {
    responseType: "blob",
  });
};

export const uploadDocument = async ({
  service,
  file,
  fileType,
  transactionId,
}: {
  service: string;
  file: File;
  fileType: string;
  transactionId?: string;
}): Promise<{ documentUrl: string; fileType: string }> => {
  const formData = new FormData();
  formData.append("file", file);

  const encodedFileType = encodeURIComponent(fileType);
  const response = await api.post<UploadedDocument>(
    service,
    `documents?fileType=${encodedFileType}`,
    formData,
    ...(transactionId ? [{ transactionId }] : []),
  );

  const documentUrl = response?.documentUrl ?? "";

  return { documentUrl, fileType };
};

export const downloadDocument = async ({
  service,
  url,
}: {
  service: string;
  url: string;
}): Promise<Blob> => {
  let encodedUrl = "";
  try {
    encodedUrl = encodeURIComponent(decodeURIComponent(url));
  } catch {
    encodedUrl = encodeURIComponent(url);
  }
  return api.get(service, `documents?url=${encodedUrl}`, {
    responseType: "blob",
  });
};

export const uploadAdditionalDocuments = async ({
  service,
  files,
  transactionId,
}: {
  service: string;
  files: File[];
  transactionId?: string;
}): Promise<string[]> => {
  const formData = new FormData();
  files.forEach((file) => {
    if (file instanceof File) {
      formData.append("files", file);
    }
  });

  const { documentUrls } = await api.post<{ documentUrls: string[] }>(
    service,
    `additional-documents`,
    formData,
    ...(transactionId ? [{ transactionId }] : []),
  );

  return documentUrls;
};

export const getAuthenticatedWriteSasToken = async ({
  contentType,
  prefixes,
}: {
  contentType: string | null;
  prefixes: readonly SasTokenPrefix[];
}): Promise<SasTokenResponse> => {
  let lastError: unknown;

  for (const prefix of prefixes) {
    try {
      return await getSasToken({
        privilege: SasTokenPrivilege.WRITE,
        prefix,
        contentType,
        blobUrl: null,
      });
    } catch (error) {
      lastError = error;

      if (getErrorStatus(error) !== 400) {
        throw error;
      }
    }
  }

  throw lastError;
};

export const uploadAuthenticatedDocumentToAzure = async ({
  file,
  prefixes,
  certificateType,
}: {
  file: File;
  prefixes: readonly SasTokenPrefix[];
  certificateType: string;
  transactionId?: string;
}): Promise<{ documentUrl: string; certificateType: string }> => {
  const { blobUrl, sasToken } = await getAuthenticatedWriteSasToken({
    prefixes,
    contentType: file.type || null,
  });

  const [uploadResult] = await uploadFileToAzureBlob({
    file,
    blobUrl,
    sasToken,
    certificateType,
  });

  return {
    documentUrl: uploadResult?.documentUrl ?? blobUrl,
    certificateType,
  };
};

export const uploadExternalDocument = async ({
  file,
  prefix,
  certificateType,
  transactionId,
}: {
  file: File;
  prefix: SasTokenPrefix;
  certificateType: string;
  transactionId?: string;
}): Promise<{ documentUrl: string; certificateType: string }> => {
  const { blobUrl, sasToken } = await getPublicSasToken(
    {
      privilege: SasTokenPrivilege.WRITE,
      prefix,
      contentType: file.type || null,
      blobUrl: null,
    },
    ...(transactionId ? [transactionId] : []),
  );

  const [uploadResult] = await uploadFileToAzureBlob({
    file,
    blobUrl,
    sasToken,
    certificateType,
  });

  return {
    documentUrl: uploadResult?.documentUrl ?? blobUrl,
    certificateType,
  };
};
